'use client'

import Image from 'next/image'
import { motion, useMotionValueEvent, useScroll, useTransform } from 'framer-motion'
import { usePathname } from 'next/navigation'
import { X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/utils/styles/cn'
import { SuperscaleCloverBadge } from '@/features/landing-page/v3/superscale-mark'
import { SuperscaleAgentMark } from '@/components/superscale-agent-mark/superscale-agent-mark'
import { LpDotsIndicator } from '@/features/landing-page/v3/lp-dots-indicator'
import { useLandingCarouselAssetsForSlug } from '@/features/landing-page/landing-carousel-assets-context'
import { resolveAssetSlug } from '@/features/landing-page/helpers/landing-route-content'
import type { ILandingCarouselAsset } from '@/features/landing-page/helpers/get-carousel-assets-server'
import {
    MAGIC_AGENTS,
    MAGIC_AGENTS_DESKTOP_PER_SIDE,
    MAGIC_AGENTS_MOBILE_PER_SIDE,
    type IMagicAgent,
} from '@/features/landing-page/v3/sections/magic-data'
import { LP_GRID } from '@/features/landing-page/v3/lp-grid'

/**
 * lp-v3 "Your touch / Superscale's Magic" — two beats:
 *
 * 1. PIN: heading + a full-height timeline (stretched to the bottom of the screen). While the
 *    section is pinned (CSS sticky) the orange mark scrubs down the line with scroll — nothing
 *    else moves. Only once the mark reaches the bottom does the pin release.
 * 2. REVEAL: normal scrolling then brings in the creative stack + flanking agent marks. The stack
 *    is NOT scroll-driven — dots (or swipe) flip through it. Clicking it expands the cards: on
 *    desktop into a horizontal snap carousel (same pattern as the case-study cards), on mobile into
 *    a full-screen vertical feed (scroll down through the creatives, reels-style). ✕ restacks.
 *
 * The creatives are the SAME per-landing-page carousel assets the old landing used (resolveAssetSlug →
 * carousel context), so the stack matches whatever landing page you're on.
 *
 * Scroll choreography uses framer-motion's `useScroll` (no per-frame React re-render) + CSS sticky
 * for the pin. All content ships in the HTML (SEO); under reduced-motion / no-JS it lays out in
 * normal flow (finished timeline, stack navigable via dots/swipe). `!` beats `.ds2`.
 */

// Show up to 11 creatives from the per-page carousel manifest (drop new assets there to add more).
const MAX_CARDS = 11
const HALF = Math.ceil(MAGIC_AGENTS.length / 2)
const LEFT_AGENTS = MAGIC_AGENTS.slice(0, HALF)
const RIGHT_AGENTS = MAGIC_AGENTS.slice(HALF)

// Agent placements, mirrored on both sides (same rows left and right). top = % of the flanking zone
// height, offset = distance from the section's outer edge (px or % of the zone width).
type ChevronPos = { top: number; offset: string }

// MOBILE (3 per side): first mark ON the edge (peeks off it, partially clipped), second a bit inside
// the screen, third back on the edge — per the mobile design reference.
const MOBILE_SCATTER: ChevronPos[] = [
    { top: 14, offset: '-38px' },
    { top: 48, offset: '0px' },
    { top: 82, offset: '-38px' },
]

// DESKTOP md–xl (5 per side): two clean PARALLEL diagonal lines marching in toward the creatives,
// with uniform spacing along each line (equal top/offset steps). The 3-agent top line uses smaller
// steps, the 2-agent lower line the same slope with larger steps.
const DESKTOP_SCATTER: ChevronPos[] = [
    // top line — 3 agents, steps of 16 top / 30% offset
    { top: 4, offset: '0%' },
    { top: 20, offset: '30%' },
    { top: 36, offset: '60%' },
    // lower line — 2 agents, same slope, larger steps (21 top / 40% offset)
    { top: 51, offset: '15%' },
    { top: 72, offset: '55%' },
]

// XL (7 per side): same two-parallel-diagonals system with room for one more agent on each line —
// a 4-agent top line and a 3-agent lower line, uniform steps along each.
const XL_SCATTER: ChevronPos[] = [
    // top line — 4 agents, steps of 13 top / 22% offset
    { top: 4, offset: '0%' },
    { top: 17, offset: '22%' },
    { top: 30, offset: '44%' },
    { top: 43, offset: '66%' },
    // lower line — 3 agents, same slope, offset in from the edge
    { top: 58, offset: '8%' },
    { top: 71, offset: '30%' },
    { top: 84, offset: '52%' },
]

// Stable id for an agent badge = its global index in MAGIC_AGENTS + role. One agent is flagged
// `defaultOpen`, so it's the first spotlight when the desktop fade rotation seeds.
const agentId = (globalIndex: number, role: string) => `${globalIndex}-${role}`
const DEFAULT_OPEN_INDEX = MAGIC_AGENTS.findIndex((agent) => agent.defaultOpen)

export function LpMagicSection() {
    const sectionRef = useRef<HTMLElement>(null)
    const lineRef = useRef<HTMLDivElement>(null)
    const stackBoxRef = useRef<HTMLDivElement>(null)
    const feedRef = useRef<HTMLDivElement>(null)
    const touchStartX = useRef<number | null>(null)
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
    // The mobile feed overlay renders its own <video> elements ON TOP of the (still-mounted) stack —
    // separate refs so the two never fight over the same slot.
    const feedVideoRefs = useRef<(HTMLVideoElement | null)[]>([])

    const [mounted, setMounted] = useState(false)
    const [reduce, setReduce] = useState(false)
    const [isDesktop, setIsDesktop] = useState(false)
    // xl screens show 2 extra agents per side — the rotation must know which set is visible.
    const [isXl, setIsXl] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    // Clicking the stack expands it — desktop: horizontal snap carousel in place; mobile: full-screen
    // vertical feed overlay. ✕ collapses back to the stack.
    const [isExpanded, setIsExpanded] = useState(false)
    // MOBILE tap-to-open expanded role (desktop uses the fade spotlight below instead).
    const [openRole, setOpenRole] = useState<string | null>(null)
    // DESKTOP fade spotlight: `shownOrder` holds the global indices of the agents currently faded IN,
    // in fade-in recency order — the LAST one is the "spotlight" that gets its role pill expanded. On
    // each tick one agent fades out (oldest) and a random hidden one fades in and becomes the new
    // spotlight, so the constellation breathes and "the one that just faded in expands".
    const [shownOrder, setShownOrder] = useState<number[]>([])
    // A hovered agent pins itself as the spotlight and pauses the rotation.
    const [hoveredId, setHoveredId] = useState<string | null>(null)
    const [isRotationPaused, setIsRotationPaused] = useState(false)
    // ...and only runs while the section is actually on screen (no background timers/re-renders).
    const [isInView, setIsInView] = useState(false)

    useEffect(() => {
        setMounted(true)
        setReduce(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        // Track the breakpoints LIVE (dev-tools viewport switches, tablet rotation) — the expanded
        // view depends on them: desktop expands to the carousel, mobile to the vertical feed.
        const mdQuery = window.matchMedia('(min-width: 768px)')
        const xlQuery = window.matchMedia('(min-width: 1280px)')
        const update = () => {
            setIsDesktop(mdQuery.matches)
            setIsXl(xlQuery.matches)
        }
        update()
        mdQuery.addEventListener('change', update)
        xlQuery.addEventListener('change', update)
        return () => {
            mdQuery.removeEventListener('change', update)
            xlQuery.removeEventListener('change', update)
        }
    }, [])

    // Track section visibility for the rotation timer (marks' canvases already pause themselves).
    useEffect(() => {
        const el = sectionRef.current
        if (!el) return
        const observer = new IntersectionObserver(([entry]) => setIsInView(entry.isIntersecting))
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    // The agents the current breakpoint actually renders, as global MAGIC_AGENTS indices (md shows 5
    // per side, xl all 7) — the fade rotation only ever touches this pool so a tick never lands on a
    // display:none agent.
    const poolIndices = useMemo(() => {
        const per = isXl ? LEFT_AGENTS.length : MAGIC_AGENTS_DESKTOP_PER_SIDE
        return [...LEFT_AGENTS.slice(0, per).map((_, i) => i), ...RIGHT_AGENTS.slice(0, per).map((_, i) => HALF + i)]
    }, [isXl])

    // The fade spotlight runs on DESKTOP only (mobile pills would overlay the stack), skipped under
    // reduced motion, paused while a pointer pins an agent or the stack is expanded, and idle while
    // offscreen. Until it's active every agent renders fully visible (opacity 1) with none expanded —
    // exactly the SSR HTML — so SSG/SEO and hydration are untouched; the fade is pure enhancement.
    const spotlightActive = mounted && isDesktop && !reduce && isInView && !isExpanded

    // Seed once when the spotlight first engages — about half the pool faded in, defaultOpen agent as
    // the first spotlight. A floor keeps the constellation from ever emptying.
    const shownFloor = Math.max(3, Math.round(poolIndices.length * 0.4))
    useEffect(() => {
        if (!spotlightActive) return
        setShownOrder((prev) => {
            if (prev.length) return prev
            const seed = poolIndices.slice(0, Math.round(poolIndices.length * 0.5))
            if (DEFAULT_OPEN_INDEX >= 0 && poolIndices.includes(DEFAULT_OPEN_INDEX)) {
                return [...seed.filter((i) => i !== DEFAULT_OPEN_INDEX), DEFAULT_OPEN_INDEX]
            }
            return seed
        })
    }, [spotlightActive, poolIndices])

    // The rotation: every ~2s, EXACTLY ONE agent appears (fades in) and becomes the new spotlight — so
    // within any two-second window only one is newly highlighted. The DISAPPEARANCE is random: 0, 1, or
    // 2 non-spotlight agents fade out per tick (down to a floor), so the visible count breathes — it
    // sometimes climbs until every agent is on screen, sometimes drops as a couple leave at once.
    useEffect(() => {
        if (!spotlightActive || isRotationPaused) return
        let timer = 0
        let cancelled = false
        const pick = (arr: number[]) => arr[Math.floor(Math.random() * arr.length)]
        const step = () => {
            setShownOrder((prev) => {
                const inPool = prev.filter((i) => poolIndices.includes(i))
                const hidden = poolIndices.filter((i) => !inPool.includes(i))
                // 1) One appears + becomes the spotlight (a real fade-in when any is hidden; otherwise
                //    everything is already shown, so re-spotlight a different one — the pill still hops).
                let order = hidden.length
                    ? [...inPool, pick(hidden)]
                    : [...inPool.filter((i) => i !== inPool.at(-1)), inPool.at(-1) as number]
                // 2) Fade out a random 0 / 1 / 2 of the NON-spotlight agents, never below the floor.
                const removeCount = Math.random() < 0.3 ? 0 : Math.random() < 0.6 ? 1 : 2
                for (let k = 0; k < removeCount && order.length > shownFloor; k++) {
                    const spotlight = order[order.length - 1]
                    const removable = order.filter((i) => i !== spotlight)
                    if (!removable.length) break
                    const victim = pick(removable)
                    order = order.filter((i) => i !== victim)
                }
                return order
            })
            if (!cancelled) timer = window.setTimeout(step, 1800 + Math.random() * 500)
        }
        timer = window.setTimeout(step, 1800 + Math.random() * 500)
        return () => {
            cancelled = true
            window.clearTimeout(timer)
        }
    }, [spotlightActive, isRotationPaused, poolIndices, shownFloor])

    // Derived desktop state: which agents are faded in (null ⇒ show all, the SSR/rest state) and which
    // one is expanded. A hovered agent overrides both — it pins visible + expanded until the pointer
    // leaves.
    const spotlightIndex = shownOrder.length ? shownOrder[shownOrder.length - 1] : DEFAULT_OPEN_INDEX
    const rotationOpenId = spotlightIndex >= 0 ? agentId(spotlightIndex, MAGIC_AGENTS[spotlightIndex].role) : null
    const desktopOpenId = spotlightActive ? (hoveredId ?? rotationOpenId) : null
    const desktopShownIds = useMemo(() => {
        if (!spotlightActive) return null
        const set = new Set(shownOrder.map((i) => agentId(i, MAGIC_AGENTS[i].role)))
        if (hoveredId) set.add(hoveredId)
        return set
    }, [spotlightActive, shownOrder, hoveredId])

    // Pointer over an agent pins it (visible + expanded) and holds the rotation; it resumes on leave.
    const handleHoverAgent = (id: string | null) => {
        setHoveredId(id)
        setIsRotationPaused(id !== null)
    }

    const pathname = usePathname()
    const slugAssets = useLandingCarouselAssetsForSlug(resolveAssetSlug(pathname))
    const media = (slugAssets?.assets ?? []).slice(0, MAX_CARDS)
    const n = media.length

    const pinActive = mounted && !reduce
    // The expanded view is breakpoint-dependent (client-only state, so no SSR mismatch): desktop
    // SPREADS the fanned stack into a 3D coverflow in place — the very same card nodes morph
    // outward, so it reads as the stack shuffling open; mobile opens the full-screen vertical feed.
    const isSpread = isExpanded && isDesktop
    const isFeedOpen = isExpanded && !isDesktop

    // Measure the front card so the coverflow spread scales to its width (no magic px constants); the
    // fallback until measured is 0, which just keeps the cards stacked (never breaks).
    const [stackW, setStackW] = useState(0)
    useEffect(() => {
        const el = stackBoxRef.current
        if (!el) return
        const ro = new ResizeObserver(([entry]) => setStackW(entry.contentRect.width))
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    // The orange fill + clover mark scrub down the (short) timeline as it scrolls up through the
    // viewport — 0 when the line first enters from the bottom, 1 once it reaches the upper area.
    // framer updates the MotionValue without a re-render.
    const { scrollYProgress } = useScroll({ target: lineRef, offset: ['start 0.85', 'end 0.45'] })
    const markTop = useTransform(scrollYProgress, [0, 1], ['0%', '100%'], { clamp: true })

    // Reveal: the stack + agents stay fully HIDDEN while the timeline scrubs, then reveal in ONE
    // animation once the timeline finishes (line scroll progress ≥ 0.98). It RE-hides when you scroll
    // back up past the line (≤ 0.85, with a hysteresis band so it can't flicker at the edge), so the
    // reveal plays again every time you scroll back down. Gated to `pinActive` so SSR /
    // reduced-motion render the stack visible.
    const [stackRevealed, setStackRevealed] = useState(false)
    useMotionValueEvent(scrollYProgress, 'change', (value) => {
        if (value >= 0.98) setStackRevealed(true)
        else if (value <= 0.85) setStackRevealed(false)
    })
    const stackHidden = pinActive && !stackRevealed

    // Only the ACTIVE card's video plays — the stack and the coverflow both centre a single card.
    // The mobile feed plays its own refs, so everything here pauses while the feed is open.
    useEffect(() => {
        videoRefs.current.forEach((video, index) => {
            if (!video) return
            if (index === activeIndex && !isFeedOpen) video.play().catch(() => {})
            else video.pause()
        })
    }, [activeIndex, n, isFeedOpen])

    // MOBILE FEED overlay — lock the page scroll behind it while it's open.
    useEffect(() => {
        if (!isFeedOpen) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previous
        }
    }, [isFeedOpen])

    // ...open the feed on the card that was on top of the stack (instant; activeIndex deliberately
    // not a dep — later changes come from the user scrolling the feed itself).
    useEffect(() => {
        if (!isFeedOpen) return
        const feed = feedRef.current
        const slide = feed?.querySelector<HTMLElement>(`[data-magic-feed-index="${activeIndex}"]`)
        if (feed && slide) feed.scrollTop = slide.offsetTop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFeedOpen])

    // ...scroll-spy: the slide filling the screen becomes active (keeps the dots + stack in sync
    // for when the feed closes).
    useEffect(() => {
        if (!isFeedOpen) return
        const feed = feedRef.current
        if (!feed) return
        const slides = Array.from(feed.querySelectorAll<HTMLElement>('[data-magic-feed-index]'))
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setActiveIndex(Number((entry.target as HTMLElement).dataset.magicFeedIndex))
                    }
                }
            },
            { root: feed, threshold: 0.6 }
        )
        slides.forEach((slide) => observer.observe(slide))
        return () => observer.disconnect()
    }, [isFeedOpen, n])

    // ...play only the visible feed slide's video.
    useEffect(() => {
        if (!isFeedOpen) return
        feedVideoRefs.current.forEach((video, index) => {
            if (!video) return
            if (index === activeIndex) video.play().catch(() => {})
            else video.pause()
        })
    }, [isFeedOpen, activeIndex])

    const goToCard = (index: number) => {
        // Wrap around — the coverflow has no hard edges, and it keeps dot/arrow/swipe paging simple.
        setActiveIndex(n ? ((index % n) + n) % n : 0)
    }

    // While the coverflow is spread: arrow keys page it, Escape restacks.
    useEffect(() => {
        if (!isSpread) return
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'ArrowRight') {
                event.preventDefault()
                goToCard(activeIndex + 1)
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault()
                goToCard(activeIndex - 1)
            } else if (event.key === 'Escape') {
                setIsExpanded(false)
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSpread, activeIndex, n])

    const onTouchStart = (event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX
    }
    const onTouchEnd = (event: React.TouchEvent) => {
        if (touchStartX.current === null) return
        const delta = event.changedTouches[0].clientX - touchStartX.current
        touchStartX.current = null
        if (Math.abs(delta) > 40) goToCard(activeIndex + (delta < 0 ? 1 : -1))
    }

    return (
        <section ref={sectionRef} className="relative bg-background pb-[120px] md:pb-[100px]">
            {/* BEAT 1 — heading, subtitle, then a SHORT timeline. Normal flow (no pin), so the stack
                sits right below with symmetric spacing; the orange fill + clover mark scrub on scroll.
                GAP is the same value above the line (subtitle → line) and below it (line → stack). */}
            <div className="flex flex-col items-center pt-[60px] md:pt-[100px]">
                <div className={`${LP_GRID.contentSection} flex flex-col items-center text-center`}>
                    <h2 className="text-[40px]! font-medium leading-[1.05]! tracking-[-0.01em]! text-foreground md:text-[64px]! lg:text-[100px]!">
                        <span className="block">Your touch</span>
                        <span className="block font-[family-name:var(--font-landing-pixel)]! text-[#FF5500]!">
                            Superscale&rsquo;s Magic
                        </span>
                    </h2>
                    <p className="mx-auto mt-3 max-w-[440px] text-[14px]! leading-[1.35]! text-[#746C64]!">
                        Purpose-built agents for the full loop: Generating production-ready, on-brand ads and launching
                        them to live accounts. Some creative out of millions our agents have already built:
                    </p>
                </div>

                {/* Short timeline; grey base + an orange "done" fill up to the clover mark, both faded at
                    the top. `mt-[7vh]` = the shared GAP (matched on the stack below). Fallback (no JS /
                    reduced motion): a fixed-height, finished line. */}
                <div
                    ref={lineRef}
                    className={cn(
                        'relative mt-[7vh] w-[2px] shrink-0 md:w-[3px]',
                        pinActive ? 'h-[22vh]' : 'h-[200px]'
                    )}
                >
                    <div className="absolute inset-0 [-webkit-mask-image:linear-gradient(to_bottom,transparent,#000_80px)] [mask-image:linear-gradient(to_bottom,transparent,#000_80px)]">
                        <div className="absolute inset-0 bg-[#D7D2C9]" />
                        <motion.div
                            className="absolute inset-x-0 top-0 bg-[#FF5500]"
                            style={{ height: pinActive ? markTop : '100%' }}
                        />
                    </div>
                    <motion.span
                        className="absolute left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 will-change-transform"
                        style={{ top: pinActive ? markTop : '100%' }}
                    >
                        {/* 36/56px ≈ the old mark+halo footprint (the halo used to overflow the box). */}
                        <SuperscaleCloverBadge className="size-9 md:size-14" />
                    </motion.span>
                </div>
            </div>

            {/* BEAT 2 — sits right below the line (`mt-[7vh]` = the same GAP as above the line). It's
                HIDDEN until the timeline finishes; then the stack fades/rises in and the agents
                CASCADE in one after another (not all at once). The container itself never changes
                opacity — each piece reveals on its own so the agents can stagger. overflow-x-clip
                contains the edge-peeking agent marks and the spread coverflow. */}
            <div className="relative mt-[7vh] w-full overflow-x-clip">
                {/* Stack ⇄ coverflow: the SAME card nodes carry BOTH a fanned-stack transform and a 3D
                    coverflow transform, written with an identical function list — so flipping `isSpread`
                    lets the 500ms transition MORPH between them and the stack "shuffles out" in place.
                    The flanking agents fade away while it's spread so the coverflow has room. */}
                <div className="flex w-full items-center justify-center px-2 md:px-10 lg:px-[60px]">
                    <div className="relative mx-auto flex w-full max-w-[1320px] items-center justify-center">
                        <div
                            className={cn(
                                'relative min-w-0 flex-1 self-stretch transition-opacity duration-300',
                                isSpread && 'pointer-events-none opacity-0'
                            )}
                        >
                            {/* Mobile: first 3 agents in the edge/inside/edge chevron. Desktop (md):
                                5 in the staggered double-diagonal. XL: all 7 on the same two lines. */}
                            <AgentColumn
                                agents={LEFT_AGENTS.slice(0, MAGIC_AGENTS_MOBILE_PER_SIDE)}
                                side="left"
                                scatter={MOBILE_SCATTER}
                                shownIds={null}
                                revealed={!stackHidden}
                                openId={openRole}
                                onToggle={setOpenRole}
                                indexBase={0}
                                className="md:hidden"
                            />
                            <AgentColumn
                                agents={LEFT_AGENTS.slice(0, MAGIC_AGENTS_DESKTOP_PER_SIDE)}
                                side="left"
                                scatter={DESKTOP_SCATTER}
                                shownIds={desktopShownIds}
                                revealed={!stackHidden}
                                openId={desktopOpenId}
                                onToggle={handleHoverAgent}
                                onHoverAgent={handleHoverAgent}
                                indexBase={0}
                                className="hidden md:block xl:hidden"
                            />
                            <AgentColumn
                                agents={LEFT_AGENTS}
                                side="left"
                                scatter={XL_SCATTER}
                                shownIds={desktopShownIds}
                                revealed={!stackHidden}
                                openId={desktopOpenId}
                                onToggle={handleHoverAgent}
                                onHoverAgent={handleHoverAgent}
                                indexBase={0}
                                className="hidden xl:block"
                            />
                        </div>

                        <motion.div
                            ref={stackBoxRef}
                            // 9:16 kept exactly: width-driven on mobile, height-driven on desktop (never
                            // both, so the card is never stretched). Perspective gives the coverflow its
                            // 3D depth. Collapsed: clicking anywhere expands; spread: cards navigate.
                            // Reveal: fades + rises in when the timeline finishes; the hide is instant
                            // (duration 0) so hydration never flashes and the reveal replays on re-scroll.
                            role="button"
                            tabIndex={0}
                            aria-label={isSpread ? 'Creatives from our agents' : 'Browse all creatives'}
                            initial={false}
                            animate={stackHidden ? { opacity: 0, y: 28 } : { opacity: 1, y: 0 }}
                            transition={stackHidden ? { duration: 0 } : { duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            onClick={() => {
                                if (!isExpanded) setIsExpanded(true)
                            }}
                            onKeyDown={(event) => {
                                if (!isExpanded && (event.key === 'Enter' || event.key === ' ')) {
                                    event.preventDefault()
                                    setIsExpanded(true)
                                }
                            }}
                            className={cn(
                                'relative z-10 aspect-9/16 w-[52vw] shrink-0 md:h-[70svh] md:max-h-[900px] md:w-auto',
                                !isExpanded && 'cursor-pointer'
                            )}
                            style={{ perspective: '1600px', pointerEvents: stackHidden ? 'none' : undefined }}
                            onTouchStart={onTouchStart}
                            onTouchEnd={onTouchEnd}
                        >
                            {media.map((asset, index) => {
                                // STACK (collapsed): fanned deck, cards alternate L/R as they go back.
                                const depth = (index - activeIndex + n) % n // 0 = front
                                const dir = depth % 2 === 1 ? 1 : -1
                                const rank = Math.ceil(depth / 2)
                                const sx = depth === 0 ? 0 : dir * (7 + rank * 6)
                                const sy = depth === 0 ? 0 : rank * 12
                                const srot = depth === 0 ? 0 : dir * (3 + rank * 1.4)
                                const sscale = 1 - depth * 0.05
                                const sOpacity = depth === 0 ? 1 : Math.max(0.28, 0.92 - depth * 0.15)
                                const sZ = 30 - depth
                                // COVERFLOW (spread): active centred + upright; neighbours angle away in
                                // 3D and recede, keeping ±3 alive. Spread distance scales to the card width.
                                // Ring offset so the coverflow LOOPS — the active card always has
                                // neighbours on BOTH sides, even when it's the first/last in the deck.
                                let offset = index - activeIndex
                                if (offset > n / 2) offset -= n
                                if (offset < -n / 2) offset += n
                                const absOff = Math.abs(offset)
                                const coverVisible = absOff <= 3
                                const cx = offset * stackW * 0.46
                                const cRotY = Math.max(-52, Math.min(52, -offset * 40))
                                const cTz = -absOff * 170
                                const cscale = 1 - absOff * 0.05
                                // Steeper fade so cards recede more the further they lay back (0.24/step,
                                // floor 0.22) — the scrim below turns this into the visible dimming.
                                const cOpacity = coverVisible
                                    ? absOff === 0
                                        ? 1
                                        : Math.max(0.22, 1 - absOff * 0.24)
                                    : 0
                                const cZ = 100 - absOff
                                // Same transform FUNCTION LIST in both modes → the switch interpolates.
                                const tx = isSpread ? cx : sx
                                const ty = isSpread ? 0 : sy
                                const tz = isSpread ? cTz : 0
                                const ry = isSpread ? cRotY : 0
                                const rz = isSpread ? 0 : srot
                                const scl = isSpread ? cscale : sscale
                                const op = isSpread ? cOpacity : sOpacity
                                const z = isSpread ? cZ : sZ
                                return (
                                    <div
                                        key={asset.src}
                                        onClick={(event) => {
                                            if (isSpread) {
                                                event.stopPropagation()
                                                if (offset !== 0) goToCard(index)
                                            }
                                        }}
                                        className="absolute inset-0 overflow-hidden rounded-[20px] bg-[#EDEBE7] shadow-[0_24px_60px_rgba(19,18,16,0.22)] transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                                        style={{
                                            transform: `translate3d(${tx}px, ${ty}px, ${tz}px) rotateY(${ry}deg) rotate(${rz}deg) scale(${scl})`,
                                            // Visible cards stay OPAQUE (no see-through to the card behind) — the
                                            // dimming is faked by the scrim below. Only fully-hidden cards (op 0)
                                            // keep real transparency so they still fade out on paging.
                                            opacity: op === 0 ? 0 : 1,
                                            zIndex: z,
                                            cursor: isSpread && offset !== 0 && coverVisible ? 'pointer' : undefined,
                                            pointerEvents: isSpread && !coverVisible ? 'none' : undefined,
                                        }}
                                        aria-hidden={isSpread ? !coverVisible : depth !== 0}
                                    >
                                        <MediaCard
                                            asset={asset}
                                            setRef={(el) => {
                                                videoRefs.current[index] = el
                                            }}
                                        />
                                        {/* Scrim that FAKES the old opacity: a solid section-colour layer whose
                                            own opacity is how much the card used to be dimmed (`1 - op`). The card
                                            underneath stays opaque, so no other card bleeds through. */}
                                        <div
                                            aria-hidden
                                            className="pointer-events-none absolute inset-0 bg-background"
                                            style={{
                                                opacity: 1 - op,
                                                transition: 'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1)',
                                            }}
                                        />
                                    </div>
                                )
                            })}
                        </motion.div>

                        <div
                            className={cn(
                                'relative min-w-0 flex-1 self-stretch transition-opacity duration-300',
                                isSpread && 'pointer-events-none opacity-0'
                            )}
                        >
                            <AgentColumn
                                agents={RIGHT_AGENTS.slice(0, MAGIC_AGENTS_MOBILE_PER_SIDE)}
                                side="right"
                                scatter={MOBILE_SCATTER}
                                shownIds={null}
                                revealed={!stackHidden}
                                openId={openRole}
                                onToggle={setOpenRole}
                                indexBase={HALF}
                                className="md:hidden"
                            />
                            <AgentColumn
                                agents={RIGHT_AGENTS.slice(0, MAGIC_AGENTS_DESKTOP_PER_SIDE)}
                                side="right"
                                scatter={DESKTOP_SCATTER}
                                shownIds={desktopShownIds}
                                revealed={!stackHidden}
                                openId={desktopOpenId}
                                onToggle={handleHoverAgent}
                                onHoverAgent={handleHoverAgent}
                                indexBase={HALF}
                                className="hidden md:block xl:hidden"
                            />
                            <AgentColumn
                                agents={RIGHT_AGENTS}
                                side="right"
                                scatter={XL_SCATTER}
                                shownIds={desktopShownIds}
                                revealed={!stackHidden}
                                openId={desktopOpenId}
                                onToggle={handleHoverAgent}
                                onHoverAgent={handleHoverAgent}
                                indexBase={HALF}
                                className="hidden xl:block"
                            />
                        </div>
                    </div>
                </div>

                {/* Centred slot below the highlighted card: the pagination dots while stacked, and the
                    close (restack) control while the coverflow is SPREAD — so the X sits right on the
                    little progress navigator instead of floating in the corner. Hidden until the stack
                    reveals (fades in with it). */}
                {n > 1 && (
                    <div
                        // Less top gap when it's the close button (it's taller than the dots) so its CENTRE
                        // lands where the step-indicator's centre was.
                        className={cn('flex justify-center', isSpread ? 'mt-[22px]' : 'mt-9')}
                        style={{
                            opacity: stackHidden ? 0 : 1,
                            transition: stackHidden ? 'none' : 'opacity 0.6s ease 0.2s',
                            pointerEvents: stackHidden ? 'none' : undefined,
                        }}
                    >
                        {isSpread ? (
                            <button
                                type="button"
                                aria-label="Back to stacked view"
                                onClick={() => setIsExpanded(false)}
                                className="flex size-9 items-center justify-center rounded-full bg-white text-foreground shadow-[0_6px_20px_rgba(19,18,16,0.20)] ring-1 ring-black/[0.06] transition-colors hover:bg-[#F0EEEA]"
                            >
                                <X className="size-4" />
                            </button>
                        ) : (
                            <LpDotsIndicator
                                count={n}
                                activeIndex={activeIndex}
                                onSelect={goToCard}
                                getLabel={(index) => `Show creative ${index + 1}`}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* MOBILE FEED — a full-screen vertical snap feed over the page (reels-style): one creative
                per screen, scroll/swipe down through them; ✕ returns to the stack. Client-only (opens
                on tap), so the SSR HTML is untouched. */}
            {isFeedOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Creatives from our agents"
                    className="fixed inset-0 z-50 bg-[#131210] md:hidden"
                >
                    <button
                        type="button"
                        aria-label="Back to stacked view"
                        onClick={() => setIsExpanded(false)}
                        className="absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full bg-white/10 text-[#FDFBF8] transition-colors active:bg-white/20"
                    >
                        <X className="size-5" />
                    </button>
                    <div
                        ref={feedRef}
                        className="h-full snap-y snap-mandatory overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {media.map((asset, index) => (
                            <div
                                key={asset.src}
                                data-magic-feed-index={index}
                                className="flex h-full snap-start items-center justify-center p-4"
                            >
                                <div className="aspect-9/16 h-[80svh] max-h-full max-w-[calc(100vw-32px)] overflow-hidden rounded-[20px] bg-[#EDEBE7]">
                                    <MediaCard
                                        asset={asset}
                                        setRef={(el) => {
                                            feedVideoRefs.current[index] = el
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    )
}

function MediaCard({ asset, setRef }: { asset: ILandingCarouselAsset; setRef: (el: HTMLVideoElement | null) => void }) {
    if (asset.type === 'video') {
        return (
            <video
                ref={setRef}
                src={asset.src}
                muted
                loop
                playsInline
                preload="metadata"
                className="size-full object-cover"
            />
        )
    }
    return (
        <Image
            src={asset.src}
            alt="Ad created by a Superscale agent"
            width={540}
            height={960}
            sizes="(max-width: 768px) 220px, 300px"
            className="size-full object-cover"
        />
    )
}

function AgentColumn({
    agents,
    side,
    scatter,
    shownIds,
    openId,
    onToggle,
    onHoverAgent,
    revealed,
    indexBase,
    className,
}: {
    agents: IMagicAgent[]
    side: 'left' | 'right'
    scatter: ChevronPos[]
    /** Agent ids currently faded IN; `null` ⇒ show all (mobile + the pre-spotlight SSR/rest state). */
    shownIds: Set<string> | null
    /** The expanded agent's id (its role pill open). */
    openId: string | null
    onToggle: (id: string | null) => void
    /** Mouse-hover pins an agent open + visible and pauses the rotation (desktop columns only). */
    onHoverAgent?: (id: string | null) => void
    /** When the section reveals, the agents CASCADE in (staggered); when it re-hides they vanish. */
    revealed: boolean
    indexBase: number
    className?: string
}) {
    return (
        <div className={cn('pointer-events-none absolute inset-0 z-20', className)}>
            {agents.map((agent, i) => {
                const pos = scatter[i] ?? { top: 10 + i * 20, offset: '8%' }
                const id = agentId(indexBase + i, agent.role)
                // `null` shownIds = show everything (never bakes opacity:0 into SSR); once the desktop
                // spotlight engages, only the faded-in agents are opaque + interactive.
                const shown = shownIds == null ? true : shownIds.has(id)
                // The reveal ENTRANCE is a separate inner layer so it can stagger (per-position delay)
                // independently of the ongoing spotlight fade on the outer layer; the two opacities
                // multiply. Instant on hide so a re-scroll replays the cascade cleanly. The 280ms base
                // holds the agents back so the STACK reveals first, then they cascade in after it.
                const entranceDelay = 280 + i * 70
                return (
                    <div
                        key={agent.role}
                        // lp-agent-float: gentle CSS drift so the marks "come alive" — per-agent
                        // duration/delay/direction are deterministic (index-based), so SSR is stable.
                        // The opacity fade (900ms) is an inline transition — it composes with the
                        // keyframed transform drift without fighting it.
                        className="lp-agent-float absolute"
                        style={
                            {
                                top: `${pos.top}%`,
                                [side]: pos.offset,
                                '--agent-dur': `${7 + ((indexBase + i) % 5) * 0.9}s`,
                                '--agent-delay': `${-(indexBase + i) * 1.1}s`,
                                '--agent-dx': `${i % 2 === 0 ? -10 : 12}px`,
                                opacity: shown ? 1 : 0,
                                transition: 'opacity 900ms ease',
                                pointerEvents: revealed && shown ? 'auto' : 'none',
                            } as React.CSSProperties
                        }
                        onPointerEnter={(event) => {
                            if (event.pointerType === 'mouse') onHoverAgent?.(id)
                        }}
                        onPointerLeave={(event) => {
                            if (event.pointerType === 'mouse') onHoverAgent?.(null)
                        }}
                    >
                        {/* Entrance layer — staggered reveal (its opacity multiplies with the spotlight
                            layer above); instant on hide so the cascade replays on re-scroll. */}
                        <div
                            style={{
                                opacity: revealed ? 1 : 0,
                                transform: revealed ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.8)',
                                transition: revealed
                                    ? `opacity 460ms ease ${entranceDelay}ms, transform 560ms cubic-bezier(0.16,1,0.3,1) ${entranceDelay}ms`
                                    : 'none',
                            }}
                        >
                            <AgentBadge
                                agent={agent}
                                // open toward the centre (where there's space), away from the flanking edge
                                openSide={side === 'left' ? 'right' : 'left'}
                                open={openId === id}
                                onToggle={() => onToggle(openId === id ? null : id)}
                            />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

function AgentBadge({
    agent,
    openSide,
    open,
    onToggle,
}: {
    agent: IMagicAgent
    openSide: 'left' | 'right'
    open: boolean
    onToggle: () => void
}) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={agent.role}
            className={cn(
                // chalk pill (#F0EEEA) appears on hover/open; ~6/24/6/6 padding + gap 10, 40px radius
                'group/agent flex items-center gap-2.5 rounded-[40px] p-1.5 transition-colors duration-200',
                openSide === 'left' && 'flex-row-reverse',
                open ? 'bg-[#F0EEEA]' : 'bg-transparent hover:bg-[#F0EEEA]'
            )}
        >
            <SuperscaleAgentMark preset={agent.preset} className="size-9 shrink-0 rounded-full md:size-11" />
            <span
                className={cn(
                    'overflow-hidden whitespace-nowrap font-[family-name:var(--font-landing-pixel)]! text-[18px]! leading-[0.8]! tracking-[-0.02em]! text-[#131210] transition-[max-width,opacity] duration-300 md:text-[24px]!',
                    openSide === 'left' ? 'pl-3.5' : 'pr-3.5',
                    open
                        ? 'max-w-[240px] opacity-100'
                        : 'max-w-0 opacity-0 group-hover/agent:max-w-[240px] group-hover/agent:opacity-100'
                )}
            >
                {agent.role}
            </span>
        </button>
    )
}
